// src/UserManagement.js
import React, { useState, useEffect, useCallback } from 'react'; // (1. เพิ่ม useCallback)
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext'; // (2. Import useAuth - ถูกต้อง)
import toast from 'react-hot-toast';

const API_URL = 'https://rys-backend.onrender.com/api';

// --- (3. ลบฟังก์ชัน getAuthHeaders เก่า (บรรทัด 11-19) ทิ้งไป) ---
// const getAuthHeaders = () => {
//   ... (โค้ดเก่าที่ใช้ user.token) ...
// };
// --------------------------------------------------------

function UserManagement() { 
  // --- States สำหรับ User Management ---
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [userList, setUserList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  
  // (States สำหรับ Modal Reset - เหมือนเดิม)
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resettingUser, setResettingUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // --- States สำหรับ Modal ลบ User ---
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null); 

  const navigate = useNavigate();
  // --- (4. ดึง getAuthHeaders "ตัวใหม่" มาจาก useAuth) ---
  const { user, getAuthHeaders } = useAuth();

  // --- (5. แก้ไข fetchUsers) ---
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      // (เรียกใช้ getAuthHeaders() จาก useAuth)
      const response = await axios.get(`${API_URL}/users`, getAuthHeaders());
      setUserList(response.data);
    } catch (err) {
      toast.error("ไม่สามารถโหลดรายชื่อผู้ใช้ได้");
    } finally {
      setLoadingUsers(false);
    }
  }, [getAuthHeaders]); // (6. เพิ่ม getAuthHeaders ใน dependency array)

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]); // (fetchUsers ถูกสร้างด้วย useCallback แล้ว)

  // --- (7. แก้ไข handleRegister) ---
  const handleRegister = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร");
      return;
    }
    try {
      // (เรียกใช้ getAuthHeaders() จาก useAuth)
      const response = await axios.post(`${API_URL}/register`, { username, password }, getAuthHeaders());
      toast.success(response.data.message);
      setUsername('');
      setPassword('');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    }
  };
  
  // (ฟังก์ชัน "เปิด" Modal ลบ)
  const handleOpenDeleteModal = (userToDelete) => {
    setDeletingUser(userToDelete);
    setIsDeleteModalOpen(true);
  };

  // --- (8. แก้ไข handleConfirmDeleteUser) ---
  const handleConfirmDeleteUser = async () => {
    if (!deletingUser) return;
    try {
      // (เรียกใช้ getAuthHeaders() จาก useAuth)
      const response = await axios.delete(`${API_URL}/users/${deletingUser.id}`, getAuthHeaders());
      toast.success(response.data.msg);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Delete failed');
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingUser(null);
    }
  };
  
  // (ฟังก์ชัน Reset Password - เหมือนเดิม)
  const handleOpenResetModal = (userToReset) => {
    setResettingUser(userToReset);
    setNewPassword('');
    setIsResetModalOpen(true);
  };
  
  // --- (9. แก้ไข handleSubmitResetPassword) ---
  const handleSubmitResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("รหัสผ่านใหม่ต้องมีอย่างน้อย 6 ตัวอักษร");
      return;
    }
    try {
      // (เรียกใช้ getAuthHeaders() จาก useAuth)
      const response = await axios.put(`${API_URL}/users/${resettingUser.id}/reset-password`, { password: newPassword }, getAuthHeaders());
      toast.success(response.data.msg);
      setIsResetModalOpen(false);
      setResettingUser(null);
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Reset failed');
    }
  };

  if (user?.role !== 'admin') {
    navigate('/');
    return null;
  }

  return (
    <>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8">
        <button
          onClick={() => navigate('/admin')} // (ปุ่มกลับไปหน้า Admin Hub)
          className="mb-6 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition duration-300"
        >
          &larr; กลับไปหน้า Admin Panel
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* ฟอร์มสร้าง User (เหมือนเดิม) */}
          <div className="lg:col-span-1">
            <div className="w-full bg-white rounded-lg shadow-xl p-8">
              <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
                ลงทะเบียนพนักงานใหม่
              </h2>
              <form onSubmit={handleRegister} className="space-y-6">
                <div>
                  <label htmlFor="username" className="block text-base font-medium text-gray-700">Username (สำหรับพนักงาน)</label>
                  <input type="text" id="username" value={username} onChange={(e) => setUsername(e.target.value)} required className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"/>
                </div>
                <div>
                  <label htmlFor="password" className="block text-base font-medium text-gray-700">Password (ชั่วคราว)</label>
                  <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"/>
                </div>
                <div>
                  <button type="submit" className="w-full px-4 py-3 font-semibold text-xl text-white bg-purple-600 rounded-md hover:bg-purple-700 transition duration-300">
                    สร้างบัญชี (Register)
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* ตาราง User (อัปเกรดปุ่ม "ลบ") */}
          <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-xl">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 border-b pb-3">
              รายชื่อผู้ใช้ทั้งหมด
            </h2>
            <div className="overflow-x-auto">
              {loadingUsers ? <p>Loading users...</p> : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase tracking-wider">รายชื่อ</th>
                      <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase tracking-wider">ตำแหน่ง</th>
                      <th className="px-6 py-3 text-right text-base font-medium text-gray-500 uppercase tracking-wider">ดำเนินการ</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {userList.map(u => (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-lg text-gray-800">
                          {u.username}
                          {/* (ป้องกัน Error หาก user หลักยังโหลดไม่เสร็จ) */}
                          {user && u.id === user.id && <span className="text-sm text-blue-600 ml-2">(You)</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-lg text-gray-600">{u.role}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-lg font-medium space-x-3">
                          <button
                            onClick={() => handleOpenResetModal(u)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Reset Password"
                          >
                            รีเซ็ตรหัสผ่าน
                          </button>
                          {/* (ป้องกัน Error หาก user หลักยังโหลดไม่เสร็จ) */}
                          {user && u.id !== user.id && (
                            <button
                              onClick={() => handleOpenDeleteModal(u)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete User"
                            >
                              ลบ
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* --- (Modal สำหรับ Reset Password - เหมือนเดิม) --- */}
      {isResetModalOpen && resettingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-xl m-4">
            <h2 className="text-2xl font-semibold mb-5 text-gray-800 border-b pb-3">
              รีเซ็ตรหัสผ่าน
            </h2>
            <p className="mb-4 text-lg">
              คุณกำลังจะตั้งรหัสผ่านใหม่สำหรับ: <strong className="text-blue-600">{resettingUser.username}</strong>
            </p>
            <form onSubmit={handleSubmitResetPassword}>
              <div>
                <label htmlFor="newPassword" className="block text-base font-medium text-gray-700">
                  รหัสผ่านใหม่ (อย่างน้อย 6 ตัวอักษร)
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-6">
                <button
                  type="button"
                  onClick={() => setIsResetModalOpen(false)}
                  className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition duration-300"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-300"
                >
                  ยืนยันการรีเซ็ต
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* --- (Modal สำหรับยืนยันการ "ลบ User") --- */}
      {isDeleteModalOpen && deletingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-xl m-4">
            <h2 className="text-2xl font-semibold mb-5 text-gray-800 border-b pb-3">
              ยืนยันการลบผู้ใช้
            </h2>
            <p className="mb-6 text-lg">
              คุณแน่ใจหรือไม่ว่าต้องการลบผู้ใช้: <br/>
              <strong className="text-red-600">{deletingUser.username}</strong>?
            </p>
            <p className="text-base text-gray-600">
              (การกระทำนี้ไม่สามารถย้อนกลับได้)
            </p>
            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition duration-300"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteUser} // (เรียกฟังก์ชันลบ User จริง)
                className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition duration-300"
              >
                ยืนยันการลบ
              </button>
            </div>
          </div>
        </div>
      )}
    </> 
  );
}

export default UserManagement;